import { Checkbox, FormControlLabel, makeStyles, Paper, Typography } from "@material-ui/core";
import { CSSProperties } from "@material-ui/core/styles/withStyles";
import DownloadIcon from "@mui/icons-material/Download";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MOBILE_WIDTH } from "../App";
import { AppContainerFullHeight } from "../components/AppContainerFullHeight";
import { AppDialog } from "../components/AppDialog";
import { AppSpinner } from "../components/AppSpinner";
import { AppToolbar, CURRENT_YEAR_MAX_DATE, DatePickerComponent, DatePickerWrapper, MIN_DATE } from "../components/AppToolbar";
import { ButtonComponent } from "../components/ButtonComponent";
import FilterDialog, { FilterDialogButton } from "../components/FilterDialog";
import { SelectComponent } from "../components/SelectComponent";
import { SimpleAutoComplete } from "../components/SimpleAutoComplete";
import { WorklogModalCreation } from "../components/Worklog/WorklogModalCreation";
import { WorklogSlackReport } from "../components/Worklog/WorklogSlackReport";
import TableBasedCrud, { ActionsMenuEntry } from "../external/ensolvers-core-frontend-mui/components/TableBaseCrud";
import NotificationService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/NotificationService";
import DateUtils from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/utils/DateUtils";
import useWindowSize from "../hooks/useWindowSize";
import useWorkLogList from "../hooks/useWorkLogList";
import WorkLog from "../models/WorkLog";
import { ColorUtils } from "../utils/ColorUtils";
import { WorlogWeeksDaysFilters } from "../utils/enums/filters/WorklogFilters";
import { generateReportAndDownload } from "../utils/FileUtils";
import OpsDateUtils from "../utils/OpsDateUtils";
import RoleUtils from "../utils/RoleUtils";
import WorklogsUtils from "../utils/WorklogsUtils";
import useTableHeight from "../hooks/useTableHeight";
import AuthService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/AuthService";
import { EMPTY_AUTOCOMPLETE } from "../dto/AutoCompleteUIDTO";
import NumberUtils from "../utils/NumberUtils";
import WorkLogService from "../services/WorkLogService";
import WorklogReportUtils from "../utils/WorklogReportUtils";
import { workLogCategory } from "../utils/enums/WorkLogCategory";
import { QuickWorklogTextInput } from "../components/Worklog/WorklogTextInput";
import { QuickEditRow } from "../components/Worklog/QuickEditRow";
import ActionButton, { ActionsMenuEntryIcons } from "../components/Worklog/ActionButton";

import MoreTimeIcon from '@mui/icons-material/MoreTime';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined';
import SendAndArchiveOutlinedIcon from '@mui/icons-material/SendAndArchiveOutlined';
import SendIcon from '@mui/icons-material/Send';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import { Box } from "@mui/material";

export default function WorkLogList() {
  const { t } = useTranslation();

  const {
    user,
    currPosition,
    workLog,
    setWorkLog,
    formDialog,
    setFormDialog,
    isEditMode,
    setIsEditMode,
    assignations,
    isLoading,
    update,
    selectedWorkLog,
    setSelectedWorkLog,
    unified,
    setUnified,
    slackReportDialog,
    setSlackReportDialog,
    holidays,
    userTimeoffs,
    selectedDate,
    isManagerOfProject,
    isAssignationActive,
    handleOnDeleteWorkLog,
    fetchWorklogData,
    userExternalId,
    handleTimeoffs,
    onlyWeekDays,
    setOnlyWeekDays,
    selectedAssignation,
    setSelectedAssignation,
    changeDate,
    setFetchUpdate
  } = useWorkLogList();
  const classes = useStyles();

  const [isOpen, setIsOpen] = useState(false);
  const firstInputRef = useRef<HTMLInputElement>(null);

  // Save the date of the worklog
  const [quickEditWorkLog, setQuickEditWorkLog] = useState<WorkLog | null>(null);
  const [shouldFocus, setShouldFocus] = useState(false);
  const workLogListRef = useRef<WorkLog[]>([]);
  const [enterPressed, setEnterPressed] = useState(false)

  const { isMobileWidth } = useWindowSize();
  const { bottomSectionRef, calculateMaxHeight } = useTableHeight();

  useEffect(() => {
    if (shouldFocus && firstInputRef.current) {
      firstInputRef.current.select();
      setShouldFocus(false);
    }
  }, [shouldFocus]);

  useEffect(() => {
    if (enterPressed) {
      handleOnQuickEditSave(quickEditWorkLog!, true);
    }
    setEnterPressed(false)
  }, [quickEditWorkLog])

  const handleDateChange = (date: Date | null) => {
    if (date && date !== selectedDate) {
      changeDate(date)
    }
  }

  useEffect(() => {
    setQuickEditWorkLog(null);
  }, [formDialog]);

  const canModifyWorklog = (): boolean => {
    return (
      RoleUtils.isAdmin() ||
      userExternalId === undefined ||
      isManagerOfProject ||
      userExternalId === user?.externalId! ||
      userExternalId === user?.coreUserExternalId ||
      selectedAssignation !== undefined
    );
  };

  const getAdditionalActions = (instance: WorkLog, shortList: boolean): ActionsMenuEntryIcons[] => {
    const isNew = !instance.externalId;

    if (!shortList) {
      return [
        {
          title: t("Send External Report to Slack"),
          action: async () => {
            const report = await WorklogsUtils.getExternalReport(instance);
            setSlackReportDialog({ reportMsg: report!, worklog: instance, channel: "EXTERNAL" });
          },
          icon:<SendIcon/>
        },
        {
          title: t("Copy External Report"),
          action: async () => {
            try {
              const report = await WorklogsUtils.getExternalReport(instance);
              navigator.clipboard.writeText(report!);
              NotificationService.info("External report copied to clipboard");
            } catch (error) {
              NotificationService.error("Unexpected error copying external report to clipboard");
            }
          },
          icon:<FileCopyIcon/>
        },
      ];
    }

    const actions =
      !isNew && selectedAssignation
        ? [
            {
              title: t("Edit"),
              action: () => {
                instance.assignationName = selectedAssignation.label;
                instance.assignationExternalId = selectedAssignation.externalId;
                setIsEditMode(true);
                setWorkLog(instance);
                setFormDialog(true);
              },
              icon:<EditIcon/>
            },
            {
              title: t("Copy Internal Report"),
              action: () => {
                try {
                  navigator.clipboard.writeText(WorklogsUtils.getInternalReport(instance));
                  NotificationService.info("Internal report copied to clipboard");
                } catch (error) {
                  NotificationService.error("Unexpected error copying internal report to clipboard");
                }
              },
              icon:<FileCopyOutlinedIcon/>
            },
            {
              title: t("Send Internal Report to Slack"),
              action: () => {
                setSlackReportDialog({
                  reportMsg: WorklogsUtils.getInternalReport(instance),
                  worklog: instance,
                  channel: "INTERNAL",
                });
              },
              icon:<SendAndArchiveOutlinedIcon/>
            },
          ]
        : isAssignationActive(instance) && selectedAssignation
        ? [
            {
              title: t("Add"),
              action: () => {
                // New Instance. Set Default Values
                instance.externalId = undefined;
                instance.hours = currPosition ? currPosition.externalHours : 8;
                instance.internalHours = currPosition ? currPosition.internalHours : 8;
                instance.adjustmentHours = 0;
                instance.bonusHours = 0;
                instance.assignationName = selectedAssignation.label;
                instance.assignationExternalId = selectedAssignation.externalId;
                instance.description = !canModifyWorklog() ? "PLACE HOLDER" : "";
                instance.internalDescription = !canModifyWorklog() ? "PLACE HOLDER" : "";
                handleTimeoffs(instance);
                setIsEditMode(false);
                setWorkLog(instance);
                setFormDialog(true);
              },
              icon:<MoreTimeIcon/>
            },
          ]
        : [];

    if (!isNew && canModifyWorklog()) {
      return [{ title: "Delete", action: () => setSelectedWorkLog(instance), icon:<DeleteOutlineIcon/>}, ...actions];
    }


    return actions;
  };

  const stylesForRowIfCondition = (instance: WorkLog): CSSProperties | undefined => {
    let styles: CSSProperties = {};

    const positionExternalHours = currPosition ? currPosition.externalHours : 8;
    const positionInternalHours = currPosition ? currPosition.internalHours : 8;
    let isTimeOff = false;

    if (userTimeoffs) {
      userTimeoffs?.forEach((timeoff) => {
        if (
          OpsDateUtils.isBetweenOrEqual(timeoff.shortStartDate, timeoff.shortEndDate, OpsDateUtils.toUTCWithSetHours(instance.shortDay))
        ) {
          isTimeOff = true;
        }
      });
    }

    // If it is a weekend/holiday/timeoff we apply a certain style
    if (instance.isWeekend) {
      styles = { ...styles, backgroundColor: ColorUtils.LIGHT_BLUE };
    } else if (instance.isHoliday) {
      styles = { ...styles, backgroundColor: ColorUtils.LIGHT_YELLOW };
    } else if (isTimeOff) {
      styles = { ...styles, backgroundColor: ColorUtils.LIGHT_GREEN };
    } else if (!!instance.externalId && (instance.hours < positionExternalHours || instance.internalHours < positionInternalHours)) {
      styles = { ...styles, backgroundColor: ColorUtils.LIGHT_ORANGE };
    } else if (!isAssignationActive(instance)) {
      styles = { ...styles, backgroundColor: ColorUtils.LIGHT_GRAY };
    } else if (onlyWeekDays === WorlogWeeksDaysFilters.WORKDAYS && instance.shortDay.getDay() === 5) {
      styles = { ...styles, borderBottom: `10px ${ColorUtils.LIGHT_GRAY} solid` };
    }

    return styles;
  };

  const renderAssignationAutocomplete = (
    <>
      <SimpleAutoComplete
        label="Assignation"
        items={assignations}
        value={selectedAssignation ? selectedAssignation : EMPTY_AUTOCOMPLETE}
        onChange={(newValue) => {
          setSelectedAssignation(newValue);
        }}
        sx={{ minWidth: 400 }}
      />
    </>
  );

  const renderSecondaryFilters = (
    <>
      <SelectComponent
        label={"Day"}
        value={onlyWeekDays}
        setValue={(status: string) => setOnlyWeekDays(status)}
        options={Object.values(WorlogWeeksDaysFilters)}
        size="small"
      />
      <DatePickerWrapper>
        <DatePickerComponent
          className={classes.datePicker}
          views="month"
          label="Month"
          value={selectedDate}
          inputFormat="MMMM"
          onChange={handleDateChange}
          style={{ margin: "0", textAlignLast: "left", maxWidth: "11em" }}
        />
        <DatePickerComponent
          className={classes.datePicker}
          views="year"
          label="Year"
          value={selectedDate}
          inputFormat="yyyy"
          style={{ marginRight: "0", textAlignLast: "left", maxWidth: "9em" }}
          minDate={MIN_DATE}
          maxDate={CURRENT_YEAR_MAX_DATE}
          onChange={handleDateChange}
        />
      </DatePickerWrapper>
    </>
  );

  const downloadDialogue = (
    <div className={classes.downloadSection}>
      <span>Download</span>
      <FormControlLabel
        label="Unified"
        control={<Checkbox checked={unified} onChange={(e) => setUnified(e.target.checked)} color="default" />}
      />
      <div className="buttons">
        <ButtonComponent
          clearStyles
          debounce
          icon={<DownloadIcon />}
          text="PDF"
          onClick={() => generateReportAndDownload("PDF_REPORT_WORKLOG", selectedDate, selectedAssignation!, user?.externalId, unified)
              .catch((error: any) => {
                if (error && error.message) {
                  NotificationService.error(error.message);
                } else {
                  NotificationService.error("Unexpected error downloading report");
                }
              })
          }
        />
        <ButtonComponent
          clearStyles
          debounce
          icon={<DownloadIcon />}
          text="XLSX"
          onClick={() => {
            generateReportAndDownload("XLSX_REPORT_WORKLOG", selectedDate, selectedAssignation!, user?.externalId, unified)
              .catch((error: any) => {
                if (error && error.message) {
                  NotificationService.error(error.message);
                } else {
                  NotificationService.error("Unexpected error downloading report");
                }
              })
          }}
        />
      </div>
    </div>
  )

  const renderFilters = () => {
    return isMobileWidth() ? (
      <div className={classes.container}>
        {renderAssignationAutocomplete}
        <FilterDialogButton text="Filters & downloads" isOpen={isOpen} setIsOpen={setIsOpen} />
      </div>
    ) : (
      <div className={classes.container}>
        <Paper className={classes.panel}>
          {renderAssignationAutocomplete}
          {renderSecondaryFilters}
        </Paper>
        {downloadDialogue}
      </div>
    );
  };

  const renderDialogFilters = () => {
    return <div className={classes.mobileFilters}>{renderSecondaryFilters}{downloadDialogue}</div>;
  };

  const handleOnQuickEditSave = async (quickEditWorkLogCreated: WorkLog, enterKeyDown?: boolean, dayOff?: boolean) => {
    let workLogCreated: WorkLog = {
      ...quickEditWorkLogCreated,
      assignationExternalId: selectedAssignation.externalId,
      assignationName: selectedAssignation.label,
      category: dayOff ? workLogCategory.DAY_OFF : workLogCategory.REGULAR,
      description: dayOff ? "Day Off" : quickEditWorkLog?.description,
      internalDescription: dayOff ? "Day Off" : quickEditWorkLog?.internalDescription,
      hours: dayOff ? 0 : quickEditWorkLogCreated.hours,
      internalHours: dayOff ? 0 : quickEditWorkLogCreated.internalHours,
      details: quickEditWorkLog?.details,
    };

    if (!workLogCreated.description || workLogCreated?.description!.trim().length === 0) {
      NotificationService.error("Description cannot be empty");
      return;
    }

    if (NumberUtils.hasDecimals(workLogCreated?.hours) || NumberUtils.hasDecimals(workLogCreated?.internalHours)) {
      NotificationService.error("Hours cannot have decimals");
      setQuickEditWorkLog(quickEditWorkLogCreated)
      return;
    }

    if (workLogCreated.adjustmentHours != null && !WorklogReportUtils.adjustmentHoursIsValid(workLogCreated)) {
      setQuickEditWorkLog(quickEditWorkLogCreated)
      return;
    }

    if (workLogCreated.bonusHours != null && !WorklogReportUtils.bonusHoursIsValid(workLogCreated, currPosition)) {
      setQuickEditWorkLog(quickEditWorkLogCreated)
      return;
    }

    // if the worklog has an externalId it means we're editing an existing one
    if (quickEditWorkLogCreated.externalId) {
      try{
        await WorkLogService.update(workLogCreated);
        NotificationService.info("Worklog updated!");
      } catch (error: any) {
        NotificationService.error(error?.response?.data?.message || "Error trying to update.");
        return;
      }
    } else {
      // Create
      try {
        quickEditWorkLogCreated.shortDay = OpsDateUtils.toUTCWithSetHours(quickEditWorkLogCreated.shortDay);
        await WorkLogService.create(workLogCreated);
        NotificationService.info("Worklog created!");
      } catch (error: any) {
        let errorMessage = "WorkLog for that day already exists!";

        if (error?.response?.data?.message) {
          errorMessage = error?.response?.data?.message;
        }

        NotificationService.error(errorMessage);
        return;
      }
    }

    // If the user pressed enter, we want to move to the next row
    if (enterKeyDown) {
      const lastDate = workLogCreated!.shortDay.getDate();
      workLogCreated!.shortDay.setDate(workLogCreated!.shortDay.getDate() + 1);

      while (OpsDateUtils.isWeekend(workLogCreated!.shortDay)) {
        workLogCreated!.shortDay.setDate(workLogCreated!.shortDay.getDate() + 1);
      }

      if (lastDate < workLogCreated.shortDay.getDate()) {
        setQuickEditWorkLog({
          ...quickEditWorkLog!,
          hours: -1, // we set this so that it triggers conditional to update the quickWorkLog
          internalHours: workLogCreated.internalHours,
          description: dayOff ? "" : workLogCreated.description,
          internalDescription: dayOff ? "" :workLogCreated.internalDescription,
          shortDay: workLogCreated.shortDay,
        });
      }
      setFetchUpdate(true);
      setShouldFocus(true);
      return;
    }

    setQuickEditWorkLog(null);
    setFetchUpdate(true);
  };

  const handleFieldUpdate = (fieldName: string, newValue: string ) => {
    if (quickEditWorkLog) {
      setQuickEditWorkLog({
        ...quickEditWorkLog,
        // Every time we blur description we copy it to internalDescription
        internalDescription: fieldName === "description" ? newValue : quickEditWorkLog.internalDescription, 
        [fieldName]: newValue
      });
    }
  };

  const isEditingRow = (instance: WorkLog): boolean => {
    return Boolean((quickEditWorkLog?.externalId && quickEditWorkLog?.externalId === instance.externalId) 
      || quickEditWorkLog?.shortDay.getDate() === instance.shortDay.getDate());
  }

  const handleEnterKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      handleOnQuickEditSave(quickEditWorkLog!, true);
    }
  }

  return (
    <>
      <AppContainerFullHeight
        bottomSectionRef={bottomSectionRef}
        topSection={
          <>
            <AppToolbar
              title={
                (user && userExternalId !== null && AuthService.getUser()?.externalId !== userExternalId
                  ? `${user.firstName} ${user.lastName} `
                  : "") + t("Worklog")
              }
            >
              {renderFilters()}
            </AppToolbar>
          </>
        }
        bottomSection={
          <Box sx={{ boxShadow: "10px 10px 20px rgb(0, 0, 0, 0.05)", borderRadius: "10px" }}>
            {isLoading ? (
              <AppSpinner />
            ) : (
              <TableBasedCrud<WorkLog>
                maxHeight={calculateMaxHeight(false)}
                stickyHeader
                disableAutoFetch
                elementName="Work Log"
                enablePagination={false}
                denseTable={canModifyWorklog()}
                forceUpdateOn={update}
                onElementClick={
                    (instance) => {
                      if (!isEditingRow(instance) && isAssignationActive(instance)) {
                        let newQuickEditWorkLog = { ...instance };

                        // If an existing workflow already exists, we show its data
                        if (!(instance.hours || instance.internalHours || instance.description)) {
                          // Check the WorkLogList, if the previous row has a workLog, we copy it to the new row
                          const previousWorkLog = workLogListRef.current.find((workLog) => workLog.shortDay.getDate() === instance.shortDay.getDate() - 1);

                          if (previousWorkLog?.description) {
                            newQuickEditWorkLog = {
                              ...newQuickEditWorkLog,
                              hours: previousWorkLog.hours,
                              internalHours: previousWorkLog.internalHours,
                              description: previousWorkLog.description,
                              internalDescription: previousWorkLog.internalDescription,
                            };
                          } else {
                            // If not, we set the default values
                            newQuickEditWorkLog = {
                              ...newQuickEditWorkLog,
                              hours: currPosition ? currPosition.externalHours : 8,
                              internalHours: currPosition ? currPosition.internalHours : 8,
                            };
                          }
                        }

                        setQuickEditWorkLog(newQuickEditWorkLog);
                        setShouldFocus(true);
                      }
                    }
                } 
                onDataFetch={(pageNumber, count) => {
                  const result =  fetchWorklogData(pageNumber, 31 /* changes on the controller and service.ts made it so we now have to hardcode this */)

                  // We use this result to be able to copy workLogs
                  if (result) {
                    result.then((data) => {
                      workLogListRef.current = data.content;
                    })
                  }
                  return result
                }}
                renderDialogContent={() => <></>}
                tableColumns={[
                  {
                    header: t("Date"),
                    content: (instance) => (
                      <span id={`day-${instance.shortDay.getDate ? instance.shortDay.getDate() : new Date(instance.shortDay).getDate()}`}>
                        {DateUtils.formatAsDDDMMM(instance?.shortDay)}
                      </span>
                    ),
                  },
                  ...(user && userExternalId !== null && AuthService.getUser()?.externalId !== userExternalId
                    ? [
                      {
                        header: t("Full Name"),
                        content: () => <>{`${user.firstName} ${user.lastName}`}</>,
                      }
                    ]
                    : []),
                  {
                    header: t("Hours"),
                    content: (instance) => {

                      if (isEditingRow(instance)) {
                        if (quickEditWorkLog?.hours === -1) {
                          setQuickEditWorkLog({
                            ...instance,
                            hours: quickEditWorkLog.internalHours, // we will use the internal hours to avoid a loop with the validation of the hours -1
                            internalHours: quickEditWorkLog.internalHours,
                            description: instance.description ? instance.description : quickEditWorkLog.description,
                            internalDescription: instance.internalDescription ? instance.internalDescription : quickEditWorkLog.internalDescription,
                          })
                          setFetchUpdate(true)
                        }
                        return (
                          <input 
                            type="number" 
                            className={classes.quickInput}
                            value={quickEditWorkLog!.hours} onChange={(e) => {
                              setQuickEditWorkLog({
                                ...quickEditWorkLog!,
                                hours: Number(e.target.value)
                              })
                            }}
                            onKeyDown={handleEnterKey}
                          />
                        )
                      } else {
                        return <>{instance?.hours > 0 ? instance.hours : ""}</>
                      }
                    }
                  },
                  {
                    header: t("Description"),
                    content: (instance) =>
                    {
                      if (isEditingRow(instance)) {
                        return (
                          <QuickWorklogTextInput
                            reference={firstInputRef}
                            className={classes.quickInputText}
                            value={quickEditWorkLog!.description || ""}
                            onFieldUpdate={(fieldName, newValue) => handleFieldUpdate(fieldName, newValue)}
                            fieldName="description"
                            onKeyDown={setEnterPressed}
                            autofocus={true}
                            />
                        )
                      } else {
                        return<>{instance.description}</>
                      }
                    }
                  },
                  {
                    header: t("Internal Hours"),
                    content: (instance) => {
                      if (isEditingRow(instance)) {
                        return (
                          <input type="number" 
                            value={quickEditWorkLog!.internalHours} 
                            className={classes.quickInput}
                            onChange={(e) => {
                              setQuickEditWorkLog({
                                ...quickEditWorkLog!,
                                internalHours: Number(e.target.value)
                              })
                            }}
                            onKeyDown={handleEnterKey}
                          />
                        )
                      } else {
                        return <>{instance?.internalHours > 0 ? instance.internalHours : ""}</>
                      }
                    }
                  },
                  RoleUtils.isAdminOrManager()
                    ? {
                        header: t("Adjustment Hours"),
                        content: (instance) => {
                          if (isEditingRow(instance)) {
                            return (
                              <input type="number" 
                                className={classes.quickInput}
                                value={quickEditWorkLog!.adjustmentHours} 
                                onChange={(e) => {
                                setQuickEditWorkLog({
                                  ...quickEditWorkLog!,
                                  adjustmentHours: Number(e.target.value)
                                })
                              }}
                              onKeyDown={handleEnterKey}
                              />
                            )
                          } else {
                            return <>{instance?.adjustmentHours > 0 ? instance.adjustmentHours : ""}</>
                          }
                        }
                      }
                    : null,
                  RoleUtils.isAdminOrManager()
                    ? {
                        header: t("Bonus Hours"),
                        content: (instance) => {
                          if (isEditingRow(instance)) {
                            return (
                              <input 
                              className={classes.quickInput}
                              type="number" value={quickEditWorkLog!.bonusHours} onChange={(e) => {
                                setQuickEditWorkLog({
                                  ...quickEditWorkLog!,
                                  bonusHours: Number(e.target.value)
                                })
                              }}
                              onKeyDown={handleEnterKey}
                              />
                            )
                          } else {
                            return <>{instance?.bonusHours > 0 ? instance.bonusHours : ""}</>
                          }
                        }
                      }
                    : null,
                  {
                    header: t("Internal Description"),
                    content: (instance) =>
                    {
                      if (isEditingRow(instance)) {
                        return (
                          <QuickWorklogTextInput
                            className={classes.quickInputText}
                            value={quickEditWorkLog!.internalDescription || ""}
                            onFieldUpdate={(fieldName, newValue) => handleFieldUpdate(fieldName, newValue)}
                            fieldName="internalDescription"
                            onKeyDown={setEnterPressed}
                            />
                        )
                      } else {
                        return<>{instance.internalDescription}</>
                      }
                    }
                  },
                  {
                    header: t("Details"),
                    content: (instance) => {
                      const details = WorklogsUtils.getWorklogDetails(instance);
                      if (isEditingRow(instance)) {
                        return (
                          <QuickEditRow
                            instance={instance}
                            quickEditWorkLog={quickEditWorkLog || undefined}
                            isEditingRow={isEditingRow}
                            handleOnQuickEditSave={handleOnQuickEditSave}
                            setQuickEditWorkLog={setQuickEditWorkLog}
                            currPosition={currPosition}
                            setShouldFocus={setShouldFocus}
                            setFechUpdate={setFetchUpdate}
                          />
                        )
                      } else {
                        return <>{instance.details ? details.length > 0 ? <span>{details.join(", ")}</span> : <span></span> : ""}</>;
                      }
                    },
                  },
                  {
                    header: t("Actions"),
                    content: (instance) => {
                      const actions = getAdditionalActions(instance, true)
                      return (<>
                        {actions.map((action) => {return (<ActionButton onClick={action.action} tooltip={action.title} icon={action.icon}/>)})}
                        </>
                      )
                    },
                  },
                ]}
                buildAdditionalActions={(instance) => getAdditionalActions(instance, false)}
                hideActions={true}  // Hide default Actions column
                stylesForRowIfCondition={stylesForRowIfCondition}
              />
            )}
          </Box>
        }
      />

      {!!workLog && (
        <WorklogModalCreation
          open={formDialog}
          setOpen={setFormDialog}
          isEditMode={isEditMode}
          setIsEditMode={setIsEditMode}
          workLog={workLog}
          setWorkLog={setWorkLog}
          holidays={holidays}
          paramUserExternalId={userExternalId!}
          user={user!}
          position={currPosition}
          setFetchUpdate={setFetchUpdate}
        />
      )}

      {/* Dialog to Confirm Slack Message */}
      {slackReportDialog && <WorklogSlackReport slackReportDialog={slackReportDialog} setSlackReportDialog={setSlackReportDialog} />}

      {/* Dialog to delete work logs */}
      <AppDialog
        open={!!selectedWorkLog}
        onClose={() => setSelectedWorkLog(undefined)}
        title={"Delete"}
        content={
          <Typography>
            {`The next worklog is going to be eliminated: ${DateUtils.formatAsDDDMMMYYY(selectedWorkLog?.shortDay!)} - ${
              selectedWorkLog?.description
            }. 
            Do you want to confirm this action?`}
          </Typography>
        }
        buttons={[{ label: "Confirm delete", onClick: () => handleOnDeleteWorkLog() }]}
      />
      {/* Dialog to manage table filters on mobile view */}
      <FilterDialog title="Worklog" useCustomStyle={true} isOpen={isOpen} setIsOpen={setIsOpen}>
        {renderDialogFilters()}
      </FilterDialog>
    </>
  );
}

const useStyles = makeStyles((theme) => ({
  panel: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '1rem',
    border: `1px solid ${ColorUtils.LIGHTER_GRAY}`,
    boxSizing: 'inherit',
    boxShadow: "10px 10px 20px rgb(0, 0, 0, 0.05)",
    minWidth: '300px', // Set a minimum width
  },
  container: {
    position: "relative",
    width: "100%",
    padding: "5px 5px 5px 0px",
    display: "flex",
    columnGap: "10px",
  },
  datePicker: {
    "& button": {
      padding: "0 !important",
      marginRight: "-9px !important",
    },
    marginRight: "0",
    width: "10rem !important",
  },
  mobileFilters: {
    display: "flex",
    padding: "25px 15px",
    flexDirection: "column",
    rowGap: "20px",
    "& > div": {
      width: "100% !important",
      margin: "0 !important",
    },
    "&> div:nth-child(2)": {
      display: "flex !important",
      flexDirection: "row",
      columnGap: "10px",
    },
  },
  quickInput: {
    maxWidth: "3rem",
    height : "3rem", 
    padding: "0 0 0 0.5rem",
    borderRadius: "0.5rem", 
    border: `1.5px solid ${ColorUtils.TABLE_BORDER}`,
  },
  quickInputText: {
    width: "100%",
    height : "3rem",
    padding: "0 0 0 0.5rem",
    borderRadius: "0.5rem", 
    border: `1.5px solid ${ColorUtils.TABLE_BORDER}`,
  },
  downloadSection: {
    position: "absolute",
    border: "rgba(0, 0, 0, 0.23) solid 1px",
    borderRadius: "4px",
    display: "flex",
    flexDirection: "column",
    padding: ".2rem 1rem",
    alignSelf: "flex-end",
    top: "-2.2rem",
    right: ".3rem",
    paddingTop: '0.6rem',
    paddingBottom: '0.3rem',
    "&> span": {
      position: "absolute",
      top: "-.7rem",
      left: ".5rem",
      backgroundColor: "white",
      padding: ".1rem",
      color: "rgba(0, 0, 0, 0.6)",
      fontSize: ".8rem",
    },
    "&> .buttons": {
      display: "flex",
      columnGap: "1rem",
      marginTop: "0.1rem"
    },
    "&> label": {
      marginLeft: '-.3rem'
    },
  },
  [theme.breakpoints.down(1400)]: {
    panel: {
      display: "grid",
      gridTemplateColumns: "45% 15% 15% 15%",
      gridRowGap: "1px",
      "& >  div:first-child": {
        width: "20% important",
      },
      "&>div": {
        minWidth: "0 !important",
        margin: "0 !important",
      },
      width: "80% !important",
    },
    datePicker: { width: "100% !important" },
  },
  [theme.breakpoints.down(1000)]: {
    panel: {
      gridTemplateColumns: "40% 20% 18% 15% ",
    },
    container: {
      display: "grid",
      gridRowGap: "10px",
    },
    downloadSection: {
      right: "0",
      width: "250px",
      position: "relative",
      top: "0",
      flexDirection: "row",
      paddingLeft: "1.2rem",
    },
  },
  [theme.breakpoints.down(MOBILE_WIDTH)]: {
    container: {
      display: "flex",
      flexDirection: "column",
      rowGap: "10px",
      width: "100%",
      "& div": {
        minWidth: "0",
      },
    },
  },
}),
{ index: 1 }
);
